import * as React from "react";
import { useEffect, useState } from "react";
import { apiClient } from "src/api/apiService";
import {
  formDataToFormikMapper,
  sanitizeDuplicateSourceRequestData,
} from "src/api/apiHelper";
import FormContext, { IVisibleBlocksState } from "src/context/FormContext";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { Formik, FormikErrors } from "formik";
import { Box } from "@mui/material";
import { AccordionGroup } from "@origin-digital/ods-core";
import { getFormattedAddress } from "../Premises/helpers";
import { formSectionService } from "../../services/formSectionService";
import {
  ConnectionTypeEnum,
  RequestStatusEnum,
  ServiceTypeEnum,
} from "../Premises/types";
import PremiseBlock from "./PremiseBlock";
import AccountBlock from "./AccountBlock";
import {
  closeTransferInitialValues,
  formInitValues,
  formValidationSchema,
  homeUserFormValidationSchema,
  premiseDraftValidationSchema,
} from "./FormComponents/Validations";
import { IFormFunctionProps } from "./FormAccordionContainer";
import ElectricityBlock from "./ElectricityBlock";
import FormSection from "./FormSection";
import { FormValues, GasOptionEnum, Service, ServiceType } from "./types";
import {
  getGasServicesContext,
  getElectricityServiceContext,
  getIsAccountReadOnly,
  getIsHomeUserAccountReadOnly,
  getCommSentDate,
  getInternetServiceContext,
} from "./helpers";
import LodgedServiceWrapper from "./LodgedServiceWrapper";
import { MessageContextProvider } from "./Messages/MessageContext";
import {
  IMessageMetadata,
  initialMessageMetadata,
  initialInternetMessageMetadata,
  MessageTypeEnum,
} from "./Messages/types";
import GasLineBlock from "./GasLineBlock";
import { fromServiceToMessageMetadata } from "./Messages/helpers";
import ReadOnlyElectricityBlock from "./ReadOnlyElectricityBlock";
import ReadOnlyAccountBlock from "./ReadOnlyAccountBlock";
import GasBothBlock from "./GasBothBlock";
import GasMeterBlock from "./GasMeterBlock";
import ReadOnlyGasLineBlock from "./ReadOnlyGasLineBlock";
import ReadOnlyGasBothBlock from "./ReadOnlyGasBothBlock";
import ReadOnlyGasMeterBlock from "./ReadOnlyGasMeterBlock";
import { CloseTransferForm } from "./CloseTransfer/CloseTransferForm";
import { CloseTransferData } from "./CloseTransfer/types";
import useScrollSpy from "./SideBar/useScrollSpy";
import { determineInitialExpandedBlocks } from "./helper";
import DBFormPremiseBlock from "./DBFormPremiseBlock";
import HomeUserPremiseBlock from "./HomeUser/HomeUserPremiseBlock";
import { usePermissions } from "src/hooks/usePermissions";
import { BoxedSpinner } from "../FeedContainer/BoxedSpinner";
import InternetBlock from "./InternetBlock";
import ReadOnlyInternetBlock from "./ReadOnlyInternetBlock";

const AccordionItem = (props: IFormFunctionProps) => {
  const location = useLocation();
  const duplicateSourceRequestId = location.state?.duplicateSourceRequestId;
  const [isDuplicate, setIsDuplicate] = useState(location.state?.isDuplicate);
  const [rawFormLoadData, setRawFormLoadData] = useState<any | null>(null);
  const [formLoadValues, setFormLoadValues] =
    useState<FormValues>(formInitValues);
  const [premiseId, setPremiseId] = useState<string | null>(null);
  const [savingBlock, setSavingBlock] = useState(false);
  const [electricityMessageMetadata, setElectricityMessageMetadata] =
    useState<IMessageMetadata>(initialMessageMetadata);
  const [gasLineMessageMetadata, setGasLineMessageMetadata] =
    useState<IMessageMetadata>(initialMessageMetadata);
  const [gasBothMessageMetadata, setGasBothMessageMetadata] =
    useState<IMessageMetadata>(initialMessageMetadata);
  const [gasMeterMessageMetadata, setGasMeterMessageMetadata] =
    useState<IMessageMetadata>(initialMessageMetadata);
  const [internetMessageMetadata, setInternetMessageMetadata] =
    useState<IMessageMetadata>(initialInternetMessageMetadata);
  const { requestId, source } = useParams<{
    requestId: string;
    source: string;
  }>();
  const [searchParams] = useSearchParams();
  const connectionType = searchParams.get("connection");
  //Set up the form context to share state between blocks
  const {
    setFormState,
    formState,
    setDisplayingSnackbar,
    setVisibleBlocks,
    visibleBlocks,
    setCurrentlyActiveBlock,
    setBlocksExpanded,
    blocksExpanded,
    requestDetailsLoaded,
    setRequestDetailsLoaded,
    setOnlineAccountHolderDetails,
  } = React.useContext(FormContext);
  const {
    premiseBlockRef,
    accountBlockRef,
    electricityBlockRef,
    gasLineBlockRef,
    gasBothBlockRef,
    gasMeterBlockRef,
    internetBlockRef,
    closeTransferBlockRef,
  } = props;
  const visibleSections = Object.keys(visibleBlocks);

  const active = useScrollSpy({
    items: visibleSections.filter(
      (section) => visibleBlocks[section as keyof IVisibleBlocksState]
    ),
  });

  useEffect(() => {
    setCurrentlyActiveBlock(active);
  }, [active]);

  useEffect(() => {
    const getDuplicateDetails = async () => {
      try {
        const { data } = await apiClient.get(
          `/draft/request/${duplicateSourceRequestId}`
        );
        setSavingBlock(false);
        const sanitisedData = sanitizeDuplicateSourceRequestData(data);
        setFormLoadValues(formDataToFormikMapper(sanitisedData));
        const { services, premise } = sanitisedData;
        const { site_details } = premise;

        const electricityService = services.find(
          (s: Service) => s.service_type === "ELECTRICITY"
        );
        const elecServicesForContext =
          getElectricityServiceContext(electricityService);
        const gasServicesForContext = getGasServicesContext(services);

        setFormState((prevState) => ({
          ...prevState,
          state: site_details.state,
          siteDetails: { ...site_details, id: undefined },
          msaNumber: site_details.requesting_company?.msa_number
            ? site_details.requesting_company.msa_number
            : null,
          postcode: site_details.postcode,
          electricityService: elecServicesForContext,
          gasServices: gasServicesForContext,
          commSentDateTime: getCommSentDate(services),
        }));

        setRequestDetailsLoaded(true);
      } catch (e) {
        console.error("Failed to fetch draft:", e);
        setDisplayingSnackbar({
          message: `Couldn't fetch premise`,
          tone: "caution",
        });
      }
    };
    const getDraftDetails = async () => {
      if (!requestId) {
        setRequestDetailsLoaded(true);
        return;
      }
      try {
        const { data } = await apiClient.get(`/draft/request/${requestId}`);
        setSavingBlock(false);
        setRawFormLoadData(data);
        setFormLoadValues(formDataToFormikMapper(data));
        setPremiseId(data.premise.id);

        const { services, premise } = data;
        const { site_details } = premise;

        const electricityService = services.find(
          (s: Service) => s.service_type === "ELECTRICITY"
        );

        const gasServices = {
          gasLine: services.find(
            (s: Service) =>
              s.service_type === "GAS" &&
              s.connection_type === ConnectionTypeEnum.SERVICE_CONNECTION
          ),
          both: services.find(
            (s: Service) =>
              s.service_type === "GAS" &&
              s.connection_type ===
                ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX
          ),
          gasMeter: services.find(
            (s: Service) =>
              s.service_type === "GAS" &&
              s.connection_type === ConnectionTypeEnum.METER_FIX
          ),
        };
        const internetService = services.find(
          (s: Service) => s.service_type === ServiceType.INTERNET
        );
        const internetServiceForContext =
          getInternetServiceContext(internetService);

        const elecServicesForContext =
          getElectricityServiceContext(electricityService);
        const gasServicesForContext = getGasServicesContext(services);

        setFormState((prevState) => ({
          ...prevState,
          state: site_details.state,
          siteDetails: site_details,
          address: getFormattedAddress(premise),
          msaNumber: site_details.requesting_company?.msa_number
            ? site_details.requesting_company.msa_number
            : null,
          postcode: site_details.postcode,
          electricityService: elecServicesForContext,
          gasServices: gasServicesForContext,
          internetService: internetServiceForContext,
          isAccountReadOnly:
            isHomeUserFromPermissions ||
            (data.premise.id && !data.premise.site_details.requesting_company)
              ? getIsHomeUserAccountReadOnly(services)
              : getIsAccountReadOnly(services, isCreatedByHomeUser),
          commSentDateTime: getCommSentDate(services),
        }));

        // Update message metadata for each service
        const updateMessageMetadata = (
          service: Service | undefined,
          setMetadata: React.Dispatch<React.SetStateAction<IMessageMetadata>>
        ) => {
          if (service) {
            setMetadata((prev) => ({
              ...prev,
              ...fromServiceToMessageMetadata(service),
            }));
          }
        };
        const initialExpandedBlocks = determineInitialExpandedBlocks(
          connectionType as ConnectionTypeEnum,
          elecServicesForContext,
          gasServicesForContext,
          source === "close-transfer"
        );
        setBlocksExpanded({
          ...blocksExpanded,
          ...initialExpandedBlocks,
        });

        updateMessageMetadata(
          electricityService,
          setElectricityMessageMetadata
        );
        updateMessageMetadata(gasServices.gasLine, setGasLineMessageMetadata);
        updateMessageMetadata(gasServices.both, setGasBothMessageMetadata);
        updateMessageMetadata(gasServices.gasMeter, setGasMeterMessageMetadata);
        updateMessageMetadata(internetService, setInternetMessageMetadata);
        setOnlineAccountHolderDetails({
          accountPrimaryFirstName: data.account?.first_name,
          accountPrimaryLastName: data.account?.last_name,
          accountPrimaryEmail: data.account?.email,
        });
      } catch (e) {
        console.error("Failed to fetch draft:", e);
        setDisplayingSnackbar({
          message: `Couldn't fetch premises`,
          tone: "caution",
        });
      } finally {
        setRequestDetailsLoaded(true);
      }
    };

    if (props.disableInitialEffect) return;

    if (isDuplicate) {
      getDuplicateDetails();
    } else {
      getDraftDetails();
    }
  }, [requestId, duplicateSourceRequestId, props.disableInitialEffect]);

  useEffect(() => {
    if (requestDetailsLoaded) {
      const accountBlockVisible = formSectionService.isAccountVisible(
        formLoadValues,
        premiseId,
        isHomeUserOrCreatedByHomeUser,
        isDuplicate
      );

      const electricityBlockVisible = formSectionService.isElectricityVisible(
        formState,
        formLoadValues.selectedConnections,
        isDuplicate
      );

      const gasLineBlockVisible = formSectionService.isGasVisible(
        formLoadValues.gasOptions,
        GasOptionEnum.GAS_LINE,
        formState.gasServices?.gasLine?.draftId,
        formState.gasServices?.gasLine?.isReadOnly,
        isDuplicate
      );

      const gasBothBlockVisible = formSectionService.isGasVisible(
        formLoadValues.gasOptions,
        GasOptionEnum.BOTH,
        formState.gasServices?.both?.draftId,
        formState.gasServices?.both?.isReadOnly,
        isDuplicate
      );

      const gasMeterBlockVisible = formSectionService.isGasVisible(
        formLoadValues.gasOptions,
        GasOptionEnum.GAS_METER,
        formState.gasServices?.gasMeter?.draftId,
        formState.gasServices?.gasMeter?.isReadOnly,
        isDuplicate
      );
      const internetBlockVisible = formSectionService.isInternetVisible(
        formState,
        formLoadValues.selectedConnections,
        isDuplicate
      );
      setVisibleBlocks((prevState) => ({
        ...prevState,
        accountBlock: accountBlockVisible,
        electricityBlock: electricityBlockVisible,
        gasLineBlock: gasLineBlockVisible,
        gasBothBlock: gasBothBlockVisible,
        gasMeterBlock: gasMeterBlockVisible,
        internetBlock: internetBlockVisible,
        closeTransferBlock: isFromCloseTransfer,
      }));
    }
  }, [formLoadValues]);

  const isFromCloseTransfer =
    source === "close-transfer" ||
    [
      formState.gasServices?.both?.requestStatus,
      formState.gasServices?.gasMeter?.requestStatus,
      formState.electricityService?.requestStatus,
    ].some((requestStatus) =>
      [
        RequestStatusEnum.CT_SUBMITTED,
        RequestStatusEnum.PROCESSED,
        RequestStatusEnum.AWAITING_ACCOUNT_HOLDER,
      ].includes(requestStatus as RequestStatusEnum)
    );

  const getCloseTransferMessageMetaInfo = (_requestId?: string) => {
    if (!_requestId) {
      return {
        showMessage: false,
        messageMetadata: undefined,
      };
    }
    //gasLine is not included as it is not a close and transfer service
    const allServices = [
      formState.gasServices?.both,
      formState.gasServices?.gasMeter,
      formState.electricityService,
    ];
    const chosenService = allServices.find(
      (service) => service?.requestId === _requestId
    );
    const chosenServiceRequestStatus = chosenService?.requestStatus;
    const chosenServiceOriginalMessageMetadata = [
      electricityMessageMetadata,
      gasMeterMessageMetadata,
      gasBothMessageMetadata,
      internetMessageMetadata,
    ].find((metadata) => metadata?.requestId === _requestId);
    const chosenServiceMessageMetadata = {
      ...chosenServiceOriginalMessageMetadata,
      type: MessageTypeEnum.CLOSE_AND_TRANSFER,
    };
    return {
      showMessage:
        chosenServiceRequestStatus === RequestStatusEnum.CT_SUBMITTED ||
        chosenServiceRequestStatus === RequestStatusEnum.PROCESSED ||
        chosenServiceRequestStatus ===
          RequestStatusEnum.AWAITING_ACCOUNT_HOLDER,
      messageMetadata: chosenServiceMessageMetadata,
    };
  };

  const {
    showMessage: showCloseTransferMessage,
    messageMetadata: closeTransferMessageMetadata,
  } = getCloseTransferMessageMetaInfo(requestId ?? "");

  useEffect(() => {
    if (isFromCloseTransfer && premiseId) {
      apiClient
        .get<CloseTransferData[]>(`/close-transfer/${premiseId}`)
        .then(({ data }) => {
          setFormLoadValues((prevFormLoadValues: FormValues) => ({
            ...prevFormLoadValues,
            closeTransfer: mapCloseTransferToFormValues(data),
          }));
          setFormState((prevState) => ({
            ...prevState,
            closeTransfer: {
              [ServiceTypeEnum.GAS]: data.find(
                (closeTransfer) =>
                  closeTransfer.service_type === ServiceTypeEnum.GAS
              ),
              [ServiceTypeEnum.ELECTRICITY]: data.find(
                (closeTransfer) =>
                  closeTransfer.service_type === ServiceTypeEnum.ELECTRICITY
              ),
            },
          }));
        });
    }
  }, [premiseId, isFromCloseTransfer]);

  const mapCloseTransferToFormValues = (
    closeTransfers: CloseTransferData[]
  ) => {
    if (closeTransfers.length === 0) {
      return closeTransferInitialValues;
    }

    const gasPreferDate = closeTransfers.find(
      (closeTransfer) => closeTransfer.service_type === ServiceTypeEnum.GAS
    )?.earliest_date;

    const elecPreferDate = closeTransfers.find(
      (closeTransfer) =>
        closeTransfer.service_type === ServiceTypeEnum.ELECTRICITY
    )?.earliest_date;

    const fileMeta = closeTransfers.find(
      (closeTransfer) => closeTransfer.attachment_meta
    )?.attachment_meta;

    const first_name = closeTransfers.find(
      (closeTransfer) => closeTransfer.customer_first_name
    )?.customer_first_name;

    const last_name = closeTransfers.find(
      (closeTransfer) => closeTransfer.customer_last_name
    )?.customer_last_name;

    const email = closeTransfers.find(
      (closeTransfer) => closeTransfer.customer_email
    )?.customer_email;

    const phone = closeTransfers.find(
      (closeTransfer) => closeTransfer.customer_phone_number
    )?.customer_phone_number;

    const editable = !(!!first_name && !!last_name && !!email);

    return {
      isAccountHolderEditable: editable,
      serviceTypes: closeTransfers.map(
        (closeTransfer) => closeTransfer.service_type
      ),
      handoverType: closeTransfers[0].chosen_action,
      gasPreferDate: gasPreferDate ? new Date(gasPreferDate) : null,
      elecPreferDate: elecPreferDate ? new Date(elecPreferDate) : null,
      fileMeta: fileMeta && {
        name: fileMeta.file_name,
        fileType: fileMeta.file_upload_type,
        link: fileMeta.link,
        attachmentId: fileMeta.id,
      },
      accountHolder: {
        firstName: first_name,
        lastName: last_name,
        phone,
        email,
      },
      isShowCustomerConsentCheckbox: !first_name && !last_name && !email,
    };
  };
  const { isHomeUser: isHomeUserFromPermissions } = usePermissions();
  const isCreatedByHomeUser = !!(
    premiseId && !formLoadValues.requestingCompany
  );
  const isHomeUserOrCreatedByHomeUser =
    isHomeUserFromPermissions || // this checks if logged-in user is a home user, use this only if you only need to check if logged-in user is home user
    isCreatedByHomeUser; // this checks if the request was created by a home user initially

  const determinePremiseBlock = (
    values: FormValues,
    setFieldValue: (
      field: string,
      value: any
    ) => Promise<void | FormikErrors<any>>,
    submitForm: () => void,
    validateForm: () => Promise<FormikErrors<any>>,
    homeUser: boolean,
    errors: any
  ) => {
    if (!homeUser) {
      return (
        <PremiseBlock
          setFieldValue={setFieldValue}
          submitForm={submitForm}
          savingBlock={savingBlock}
          validateForm={validateForm}
          errors={errors}
          servicesSelected={values.selectedConnections}
          state={values.premiseAddress.state}
          postcode={values.premiseAddress.postcode}
          setPremiseId={setPremiseId}
          premiseId={premiseId}
          setFormLoadValues={setFormLoadValues}
          gasOptions={values.gasOptions}
          setIsDuplicate={setIsDuplicate}
          isDuplicate={isDuplicate}
          duplicateSourceRequestId={duplicateSourceRequestId}
          homeUser={isHomeUserOrCreatedByHomeUser}
          {...props}
        />
      );
    }
    return (
      <HomeUserPremiseBlock
        setFieldValue={setFieldValue}
        submitForm={submitForm}
        savingBlock={savingBlock}
        validateForm={validateForm}
        errors={errors}
        servicesSelected={values.selectedConnections}
        state={values.premiseAddress.state}
        postcode={values.premiseAddress.postcode}
        setPremiseId={setPremiseId}
        premiseId={premiseId}
        setFormLoadValues={setFormLoadValues}
        gasOptions={values.gasOptions}
        rawFormLoadData={rawFormLoadData}
        homeUser={isHomeUserOrCreatedByHomeUser}
        {...props}
      />
    );
  };

  const determineValidationSchema = () => {
    if (premiseId) {
      if (isHomeUserOrCreatedByHomeUser) {
        return homeUserFormValidationSchema;
      }
      return formValidationSchema;
    }
    return premiseDraftValidationSchema;
  };

  return (
    <>
      <Formik<FormValues>
        validationSchema={determineValidationSchema()}
        initialValues={{
          state: formState.state,
          companyName: "",
          ...formLoadValues,
        }}
        onSubmit={() => {}}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize={true}
      >
        {({ setFieldValue, submitForm, validateForm, errors, values }) =>
          requestDetailsLoaded ? (
            <>
              <AccordionGroup variant="outlined" space="small">
                <FormSection
                  id="premise-block"
                  title="Premises"
                  outerRef={premiseBlockRef}
                >
                  {formState.electricityService?.channel === "NETWORK" ? (
                    <DBFormPremiseBlock />
                  ) : (
                    determinePremiseBlock(
                      values,
                      setFieldValue,
                      submitForm,
                      validateForm,
                      isHomeUserOrCreatedByHomeUser,
                      errors
                    )
                  )}
                </FormSection>
                <FormSection
                  id="account-block"
                  title="Account"
                  visible={formSectionService.isAccountVisible(
                    values,
                    premiseId,
                    isHomeUserOrCreatedByHomeUser,
                    isDuplicate
                  )}
                  outerRef={accountBlockRef}
                >
                  {formState.isAccountReadOnly ? (
                    <ReadOnlyAccountBlock />
                  ) : (
                    <AccountBlock
                      premiseId={premiseId}
                      homeUser={isHomeUserOrCreatedByHomeUser}
                    />
                  )}
                </FormSection>
                <FormSection
                  id="electricity-block"
                  title="Electricity"
                  service={formState.electricityService}
                  visible={formSectionService.isElectricityVisible(
                    formState,
                    values.selectedConnections,
                    isDuplicate
                  )}
                  outerRef={electricityBlockRef}
                >
                  {formState.electricityService?.isReadOnly ? (
                    <MessageContextProvider>
                      <LodgedServiceWrapper
                        messageMetadata={electricityMessageMetadata}
                        blockRefId="electricity-block"
                        children={<ReadOnlyElectricityBlock />}
                      />
                    </MessageContextProvider>
                  ) : (
                    <ElectricityBlock
                      setFormLoadValues={setFormLoadValues}
                      homeUser={isHomeUserOrCreatedByHomeUser}
                      premiseId={premiseId}
                    />
                  )}
                </FormSection>
                {/* Gas Line Block */}
                <FormSection
                  id="gas-line-block"
                  title={formSectionService.getGasTitle(
                    values.premiseAddress.state,
                    GasOptionEnum.GAS_LINE,
                    values.premiseAddress.postcode
                  )}
                  service={formState.gasServices?.gasLine}
                  visible={formSectionService.isGasVisible(
                    values.gasOptions,
                    GasOptionEnum.GAS_LINE,
                    formState.gasServices?.gasLine?.draftId,
                    formState.gasServices?.gasLine?.isReadOnly,
                    isDuplicate
                  )}
                  outerRef={gasLineBlockRef}
                >
                  {formState.gasServices?.gasLine?.isReadOnly ? (
                    <MessageContextProvider>
                      <LodgedServiceWrapper
                        messageMetadata={gasLineMessageMetadata}
                        blockRefId="gas-line-block"
                        children={<ReadOnlyGasLineBlock />}
                      />
                    </MessageContextProvider>
                  ) : (
                    <GasLineBlock
                      setFormLoadValues={setFormLoadValues}
                      homeUser={isHomeUserOrCreatedByHomeUser}
                      premiseId={premiseId}
                    />
                  )}
                </FormSection>
                {/* Gas Both Block */}
                <FormSection
                  id="gas-both-block"
                  title={formSectionService.getGasTitle(
                    values.premiseAddress.state,
                    GasOptionEnum.BOTH,
                    values.premiseAddress.postcode
                  )}
                  service={formState.gasServices?.both}
                  visible={formSectionService.isGasVisible(
                    values.gasOptions,
                    GasOptionEnum.BOTH,
                    formState.gasServices?.both?.draftId,
                    formState.gasServices?.both?.isReadOnly,
                    isDuplicate
                  )}
                  outerRef={gasBothBlockRef}
                >
                  {formState.gasServices?.both?.isReadOnly ? (
                    <MessageContextProvider>
                      <LodgedServiceWrapper
                        messageMetadata={gasBothMessageMetadata}
                        blockRefId="gas-both-block"
                        children={<ReadOnlyGasBothBlock />}
                      />
                    </MessageContextProvider>
                  ) : (
                    <GasBothBlock
                      setFormLoadValues={setFormLoadValues}
                      homeUser={isHomeUserOrCreatedByHomeUser}
                      premiseId={premiseId}
                    />
                  )}
                </FormSection>
                {/* Gas Meter Block */}
                <FormSection
                  id="gas-meter-block"
                  title={formSectionService.getGasTitle(
                    values.premiseAddress.state,
                    GasOptionEnum.GAS_METER,
                    values.premiseAddress.postcode
                  )}
                  service={formState.gasServices?.gasMeter}
                  visible={formSectionService.isGasVisible(
                    values.gasOptions,
                    GasOptionEnum.GAS_METER,
                    formState.gasServices?.gasMeter?.draftId,
                    formState.gasServices?.gasMeter?.isReadOnly,
                    isDuplicate
                  )}
                  outerRef={gasMeterBlockRef}
                >
                  {formState.gasServices?.gasMeter?.isReadOnly ? (
                    <MessageContextProvider>
                      <LodgedServiceWrapper
                        messageMetadata={gasMeterMessageMetadata}
                        blockRefId="gas-meter-block"
                        children={<ReadOnlyGasMeterBlock />}
                      />
                    </MessageContextProvider>
                  ) : (
                    <GasMeterBlock
                      setFormLoadValues={setFormLoadValues}
                      homeUser={isHomeUserOrCreatedByHomeUser}
                      premiseId={premiseId}
                    />
                  )}
                </FormSection>
                <FormSection
                  id="internet-block"
                  title="Internet"
                  service={formState.internetService}
                  visible={formSectionService.isInternetVisible(
                    formState,
                    values.selectedConnections,
                    isDuplicate
                  )}
                  outerRef={internetBlockRef}
                >
                  {formState.internetService?.isReadOnly ? (
                    <MessageContextProvider>
                      <LodgedServiceWrapper
                        messageMetadata={internetMessageMetadata}
                        blockRefId="internet-block"
                        children={<ReadOnlyInternetBlock />}
                      />
                    </MessageContextProvider>
                  ) : (
                    <InternetBlock
                      setFormLoadValues={setFormLoadValues}
                      homeUser={isHomeUserOrCreatedByHomeUser}
                      premiseId={premiseId}
                    />
                  )}
                </FormSection>
                <FormSection
                  id="close-transfer-block"
                  title="Close/transfer"
                  visible={isFromCloseTransfer && !isDuplicate}
                  outerRef={closeTransferBlockRef}
                >
                  {showCloseTransferMessage && closeTransferMessageMetadata ? (
                    <MessageContextProvider>
                      <LodgedServiceWrapper
                        messageMetadata={closeTransferMessageMetadata}
                        blockRefId="close-transfer-block"
                        children={<CloseTransferForm />}
                      />
                    </MessageContextProvider>
                  ) : (
                    <CloseTransferForm />
                  )}
                </FormSection>
              </AccordionGroup>
            </>
          ) : (
            <Box
              justifyContent="center"
              alignItems="center"
              marginY="medium"
              style={{
                borderRadius: "8px !important",
                border: "1px solid #E3E3E3",
                width: "100%",
                backgroundColor: "white",
              }}
            >
              <BoxedSpinner />
            </Box>
          )
        }
      </Formik>
    </>
  );
};

export default AccordionItem;
